import { useEffect, useId, useMemo, useState } from 'react'
import { CheckOutlined, DownloadOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { EmptyState } from '../../../components/ui/EmptyState'
import { Input } from '../../../components/ui/Input'
import { Modal } from '../../../components/ui/Modal'
import { Skeleton } from '../../../components/ui/Skeleton'
import { Tabs } from '../../../components/ui/Tabs'
import { useToast } from '../../../components/ui/ToastProvider'
import { cx } from '../../../components/ui/cx'
import { useCartCount } from '../../../lib/cart/cart-count-context'
import { QuantityStepper } from '../../cart/QuantityStepper'
import { addCatalogResultToCart } from '../catalog-add-to-cart'
import {
  type ConfigDimensionSegment,
  type ConfigSchema,
  type SkuPicks,
  computeRangePresets,
  formatValueToken,
  gateConfigSchema,
  isValueLegal,
  validateAndComposeSku,
} from '../configurator-sku'
import { leadTimeLabel, resultDisplayName, sourceBadgeLabel } from '../format'
import type {
  CatalogResultCard,
  CatalogSeriesConfigurator,
  CatalogSeriesConfiguratorAxis,
} from '../types'
import type { ConfigurableResolveService } from '../useConfigurableResolve'
import { defaultConfigurableResolveService } from '../useConfigurableResolve'
import { RECENTLY_VIEWED_RECORD_DEBOUNCE_MS, recordCatalogRowView } from '../useRecentlyViewed'
import type { CatalogSeriesConfiguratorService } from '../useSeriesConfigurator'
import { useSeriesConfigurator } from '../useSeriesConfigurator'
import { CatalogResultThumb } from './CatalogResultThumb'
import { ResultCard } from './ResultCard'

export interface SeriesConfiguratorDrawerProps {
  readonly open: boolean
  readonly onClose: () => void
  /** The leaf category label (route param), e.g. "Locating Pins" — the
   * configurator endpoint is category-scoped, unlike the plain series-rows
   * search this drawer also calls. */
  readonly category: string
  /** The taxonomy GROUP label (`CatalogSeriesPage`'s own `groupLabel`,
   * e.g. "Linear Motion" for the "Linear Shafts" category) — UID-460 step
   * 4's spec-table "Group" row. Real data computed one hop up
   * (`useCategoryTaxonomy`); threaded through rather than re-derived here.
   * Omitted (undefined) when the category has no taxonomy group (the
   * "Other" bucket) — the spec table then omits that row entirely, same
   * "omit, don't fabricate" convention as `seriesDescription`. */
  readonly categoryGroup?: string
  readonly seriesCode: string
  readonly seriesName: string
  /** `CatalogSeries.imageUrl` (UID-460 step 2) — the series card's own real
   * image, shown until/unless a resolved row's own `sourceable.imageRef`
   * is available (see `SeriesConfiguratorHeader`). Null when the series has
   * no real image on file — never a fabricated placeholder image. */
  readonly seriesImageUrl?: string | null
  /** `CatalogSeries.description` (UID-460 step 2) — the series' own real
   * marketing blurb. Null when none is on file; omitted from render in
   * that case rather than replaced with invented copy. */
  readonly seriesDescription?: string | null
  /** Injectable for tests; defaults to the real BFF-backed
   * `GET /catalog/categories/{category}/series/{seriesCode}/configurator`. */
  readonly configuratorService?: CatalogSeriesConfiguratorService
  /** Injectable for tests; defaults to the real BFF-backed
   * `GET /catalog/configurable/{sku}` — the SAME seam `ConfiguratorCore`
   * uses, called here for the same reason (UID-460 step 4 defect fix): a
   * resolved row that still carries its OWN dimension segments needs its
   * FINAL literal SKU composed and resolved before Add to Cart, never the
   * row's raw, unmodified `catalog_sku` TEMPLATE column
   * (`resolvedCard.sourceable.catalogSku`). */
  readonly resolveService?: ConfigurableResolveService
}

/** A window with a `min`/`max`/`step` numeric grid — the structural shape
 * both a `CatalogSeriesConfiguratorAxisInterval` (a series-wide continuous
 * facet) and a `configSchema` `range` segment (one resolved row's OWN
 * dimension) share, letting {@link RangeWindowPicker} render either without
 * caring which one it's given. */
interface RangeWindow {
  readonly min: number
  readonly max: number
  readonly step: number
}

interface FieldDerivedState {
  readonly resolvedCard: CatalogResultCard | undefined
  readonly resolvedSchema: ConfigSchema | undefined
  readonly resolvedGateKind: 'pickable' | 'disabled' | undefined
  readonly dimensionEntries: ReadonlyArray<{
    readonly segment: ConfigDimensionSegment
    readonly segmentIndex: number
  }>
  readonly everyAxisPicked: boolean
  readonly everyDimensionPicked: boolean
  /** UID-460 code-review Critical — the resolved row is a GENUINELY literal,
   * already-final SKU: it resolved with no `configSchema` at all, so there
   * is nothing left to compose and `sourceable.catalogSku` IS the real part
   * number. The one shape Case A's "Recently viewed" capture may record and
   * the one shape Add to Cart may add `resolvedCard.sourceable` directly.
   *
   * Deliberately NOT `dimensionEntries.length === 0`, which was the pre-fix
   * proxy: a row whose `configSchema` is present but UNPARSEABLE
   * (`gateConfigSchema` → `disabled`, e.g. a `raw` segment or
   * `resolvable: false`) also yields zero entries while its `catalog_sku` is
   * still a broken, unparsed TEMPLATE. That third shape is neither Case A
   * (literal) nor Case B (pickable) — the drawer shows it as "Not available
   * yet" and it must stay non-addable, non-composed and non-recorded until
   * it becomes resolvable. */
  readonly isLiteralResolvedRow: boolean
  /** UID-460 step 3/4 — VERIFIED, NOT ASSUMED: `candidateCount === 1` does
   * NOT by itself mean every visible field is picked. Every AXIS is always
   * picked by the time `candidateCount` reaches 1 (an axis' `values`/
   * `intervals` floor guarantees at least 2 reachable values for any
   * UNPICKED axis, so a lone surviving row can never leave one dangling —
   * see `series-configurator-axes.ts`'s `MIN_FACET_VALUES` floor). But a
   * RESOLVED ROW'S OWN dimension segment (`dimensionEntries`, e.g. "Overall
   * Length L") is local/client-side state, entirely independent of
   * `candidateCount`, and can genuinely still be unpicked the instant a row
   * resolves via `specs` alone. "Fully configured" therefore checks BOTH,
   * not `candidateCount === 1` alone — the exact disagreement Step 3's
   * brief asked to flag if found, and Step 4's footer/Add-to-Cart gate
   * depends on the SAME single definition (computed once here, never
   * re-derived per call site so the two can't drift). */
  readonly isFullyConfigured: boolean
}

/** The one, shared derivation `SeriesConfiguratorFieldList` (chip row +
 * banner) AND the top-level footer/tabs both read from — a pure function
 * over the same three pick-state values, so "is this fully configured"
 * can never disagree between the two call sites. */
function deriveFieldState(
  configurator: CatalogSeriesConfigurator,
  appliedSpecs: Readonly<Record<string, string>>,
  appliedDims: Readonly<Record<string, number>>,
  dimensionPicks: SkuPicks,
): FieldDerivedState {
  const everyAxisPicked = configurator.axes.every(
    (axis) => axis.name in appliedSpecs || axis.name in appliedDims,
  )
  const resolvedCard = configurator.candidateCount === 1 ? configurator.resolvedCard : undefined
  const resolvedSchema = resolvedCard?.configSchema
  const resolvedGate = resolvedSchema ? gateConfigSchema(resolvedSchema) : undefined
  const dimensionEntries =
    resolvedSchema && resolvedGate?.kind === 'pickable'
      ? pickableDimensionEntries(resolvedSchema)
      : []
  const everyDimensionPicked = dimensionEntries.every((entry) =>
    dimensionPicks.has(entry.segmentIndex),
  )
  const isLiteralResolvedRow = resolvedCard !== undefined && resolvedSchema === undefined
  // UID-460 code-review Critical — the ROW half of "fully configured", split
  // out so the gate is consulted explicitly rather than inferred from an
  // empty `dimensionEntries`:
  // - no schema at all → literal, already final, nothing to configure;
  // - a PICKABLE schema → configured once every one of its segments is picked;
  // - a DISABLED (unparseable) schema → NEVER configured. Its `catalog_sku`
  //   is still a broken template and no amount of picking can finalize it,
  //   which is exactly why the field list shows "Not available yet".
  const isRowConfigured =
    isLiteralResolvedRow ||
    (resolvedCard !== undefined && resolvedGate?.kind === 'pickable' && everyDimensionPicked)
  return {
    resolvedCard,
    resolvedSchema,
    resolvedGateKind: resolvedGate?.kind,
    dimensionEntries,
    everyAxisPicked,
    everyDimensionPicked,
    isLiteralResolvedRow,
    isFullyConfigured: configurator.candidateCount === 1 && everyAxisPicked && isRowConfigured,
  }
}

/** The outer "Drawing / Specs · CAD preview · Part number" tab strip
 * (UID-460 step 4, re-verified against the legacy reference after this
 * file's Step 1 rewrite) — three FIXED tabs, mirroring `ConfiguratorCore`'s
 * own `OUTER_TABS` precedent (same labels, same order, same "Drawing/Specs"
 * default). */
const OUTER_TABS = [
  { id: 'drawing-specs', label: 'Drawing / Specs' },
  { id: 'cad-preview', label: 'CAD preview' },
  { id: 'part-number', label: 'Part number' },
] as const

/**
 * ADR-0153 D4/D5/D7 + UID-322 — the legacy series-drawer's ROW SELECTOR,
 * opened directly from a series card on `CatalogSeriesPage` for a category
 * whose `browseMode` is `series_configurator`.
 *
 * RIGHT-ANCHORED SLIDE-IN DRAWER (UID-460 live-testing fix), not a centered
 * dialog — `Modal`'s new `variant="drawer-right"`, matching the reference
 * design.
 *
 * ONE FLAT LIST of "configure options" sections, ALWAYS FULLY EXPANDED
 * (UID-460 live-testing fix, step 1) — confirmed against the real legacy
 * reference: every section shows its complete pill list all the time, with
 * no accordion/collapse state at all. Picking a value only highlights that
 * pill (selected style); nothing else in the list changes shape. Since
 * UID-322, EVERY series-level field — discrete (Head Shape) and continuous
 * (Diameter/Length) alike — comes back from the ONE `GET .../configurator`
 * call as one unified `axes` list, each carrying `values` (discrete) and/or
 * `intervals` (continuous) for the SAME field name (never two axes for one
 * field).
 *
 * ONE LIST, NEVER TWO CONFIGURATORS (UID-460 live-testing fix, step 1). The
 * moment `candidateCount` reaches 1, the response's own `resolvedCard` names
 * that row — no second fetch, ever. If that row still carries its OWN
 * continuous dimensions (P/L/B, `configSchema`, distinct from the series'
 * axes above), those dimension segments are appended as MORE sections in
 * this SAME flat list — never a second, nested `ConfiguratorCore` mounted
 * alongside it. There is exactly one place to pick any one field, ever.
 *
 * `candidateCount` drives exactly what renders below the list:
 * - `0`: an honest empty state.
 * - `1`, `resolvedCard` has no `configSchema` (already fully resolved): a
 *   plain `ResultCard` row.
 * - `1`, `resolvedCard.configSchema` is pickable: its dimension segments are
 *   already part of the list above.
 * - `1`, `resolvedCard.configSchema` is NOT pickable (a `raw` segment or
 *   non-resolvable family): an honest disabled note.
 * - `2+` with every currently-offered section picked (the rare residual-
 *   ambiguity case, ~7-16/45 series, D5): an honest compact note,
 *   deliberately NOT a browsable list — there is no safe way to enumerate
 *   those survivors (a `dims`-assisted narrowing has no `specs`-only
 *   equivalent to re-ask `/search` with).
 *
 * UID-460 step 4 — a persistent footer (unit price/ship estimate/qty/Add to
 * Cart, via `Modal`'s own `footer` slot) and a "Drawing / Specs · CAD
 * preview · Part number" tab strip below the field list, matching the
 * legacy reference. Unit price/ship estimate/Add-to-Cart all gate on the
 * SAME `isFullyConfigured` `deriveFieldState` computes for the Step 3
 * banner — never a second, looser definition (and, per the code-review
 * Critical fix, that definition consults the resolved row's `configSchema`
 * GATE, so a row rendered as the "Not available yet" note above can never be
 * simultaneously addable). Add to Cart reuses
 * `addCatalogResultToCart` verbatim (same mechanics as `ConfiguratorCore`/
 * `ResultCard`) and deliberately does NOT close the drawer afterward
 * (confirmed product decision).
 *
 * This component composes zero SKUs of its own — `ResultCard`/
 * `addCatalogResultToCart` are reused verbatim.
 */
export function SeriesConfiguratorDrawer({
  open,
  onClose,
  category,
  categoryGroup,
  seriesCode,
  seriesName,
  seriesImageUrl,
  seriesDescription,
  configuratorService,
  resolveService = defaultConfigurableResolveService,
}: SeriesConfiguratorDrawerProps) {
  const [appliedSpecs, setAppliedSpecs] = useState<Record<string, string>>({})
  const [appliedDims, setAppliedDims] = useState<Record<string, number>>({})
  // The resolved row's OWN dimension picks (its `configSchema` segments,
  // keyed by segment index) — entirely LOCAL/client-side, distinct from
  // `appliedDims` above (which narrows the SERIES' shared axes and is sent
  // to the BFF). Reset whenever a DIFFERENT row resolves, so a stale pick
  // for one row's segment index never survives onto a different row's
  // differently-shaped schema.
  const [dimensionPicks, setDimensionPicks] = useState<SkuPicks>(new Map())
  const state = useSeriesConfigurator(
    category,
    seriesCode,
    appliedSpecs,
    appliedDims,
    configuratorService,
  )
  // Stale-while-revalidate: a PICK re-fetches the configurator, but must not
  // blow away the whole list (and unmount every section) while that request
  // is in flight — only the very FIRST fetch shows the full loading skeleton
  // below. Reset whenever a caller mounts a fresh drawer for a different
  // series (`CatalogSeriesPage` keys this component on `seriesCode`, so
  // that's a remount, not a prop change here).
  const [lastReady, setLastReady] = useState<CatalogSeriesConfigurator | null>(null)

  useEffect(() => {
    if (state.status === 'ready') setLastReady(state.configurator)
  }, [state])

  const resolvedCardSku =
    state.status === 'ready' && state.configurator.candidateCount === 1
      ? state.configurator.resolvedCard?.sourceable.catalogSku
      : undefined

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally reset ONLY when the resolved row's own identity changes — the effect body doesn't need to read `resolvedCardSku`, just react to it changing.
  useEffect(() => {
    setDimensionPicks(new Map())
  }, [resolvedCardSku])

  function toggleSpec(axisName: string, value: string) {
    setAppliedSpecs((prev) => {
      if (prev[axisName] === value) {
        const next = { ...prev }
        delete next[axisName]
        return next
      }
      return { ...prev, [axisName]: value }
    })
  }

  function pickDim(axisName: string, value: number | null) {
    setAppliedDims((prev) => {
      if (value === null) {
        const next = { ...prev }
        delete next[axisName]
        return next
      }
      return { ...prev, [axisName]: value }
    })
  }

  function pickDimensionSegment(segmentIndex: number, value: number | null) {
    setDimensionPicks((prev) => {
      const next = new Map(prev)
      if (value === null) next.delete(segmentIndex)
      else next.set(segmentIndex, value)
      return next
    })
  }

  // UID-460 step 3 — the match-count banner's "Reset" control: clears every
  // pick (discrete, continuous-series-level, and the resolved row's own
  // dimension picks) back to the initial all-unpicked state in one step.
  function resetPicks() {
    setAppliedSpecs({})
    setAppliedDims({})
    setDimensionPicks(new Map())
  }

  // UID-460 step 4 — footer state. Local/client-only; no new cart mechanics.
  const [quantity, setQuantity] = useState(1)
  const [adding, setAdding] = useState(false)
  const [activeOuterTabId, setActiveOuterTabId] = useState<string>(OUTER_TABS[0].id)
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()

  const displayConfigurator = state.status === 'ready' ? state.configurator : lastReady
  const isPending = state.status === 'loading' && lastReady !== null
  const derived = displayConfigurator
    ? deriveFieldState(displayConfigurator, appliedSpecs, appliedDims, dimensionPicks)
    : undefined
  const resolvedCard = derived?.resolvedCard
  const isFullyConfigured = derived?.isFullyConfigured ?? false
  // UID-460 step 4 defect fix (display counterpart of the Add to Cart fix
  // above) — reactively compose the resolved row's OWN dimension picks into
  // its real literal SKU (the SAME `validateAndComposeSku` call, just read
  // here rather than only at Add-to-Cart click time) so "Part Number:"
  // never shows the raw bracketed template once every dimension is picked.
  // Deliberately NOT a `resolveService.resolve()` call — that network round
  // trip stays Add-to-Cart-only; a successful CLIENT-SIDE compose is enough
  // to know the string is safe to display. `null` (no dimension segments,
  // or one still unpicked/illegal) falls through to the existing
  // `resolvedCard.sourceable.catalogSku` behavior in `PartNumberValue` —
  // the incomplete-picks display is unchanged on purpose.
  // Memoized (code-review S4) on the same two inputs every other derived
  // value in this component keys off — the schema identity and the pick map —
  // rather than re-validating/re-composing on every unrelated re-render
  // (quantity, tab switch, a keystroke in the range input).
  const composedSchema = derived?.dimensionEntries.length ? derived.resolvedSchema : undefined
  const composedSku = useMemo(
    () => (composedSchema ? validateAndComposeSku(composedSchema, dimensionPicks) : null),
    [composedSchema, dimensionPicks],
  )
  // UID-460 code-review follow-up (W-B) — an UNPARSEABLE resolved schema
  // (`gateConfigSchema` → `disabled`) is a TERMINAL state: its `catalog_sku`
  // is a raw bracketed TEMPLATE, nothing the shopper can do in this drawer
  // makes it configurable, and the field list already says so with its "Not
  // available yet" note (same discriminator, sibling render below). Showing
  // that template under a "Part Number:" label would be durable
  // misinformation, so the placeholder copy is shown instead — for BOTH the
  // always-visible header line and the "Part number" tab, which share
  // `PartNumberValue`.
  //
  // Deliberately NOT the same as the `pickable`-gate PARTIAL-PICK case
  // (test-pinned, unchanged): that one is a transient state the shopper is
  // actively working through, where showing the template is informative and
  // resolves itself on the next pick. Disjoint conditions, disjoint calls.
  //
  // Review pass 3 (W-2) — asks the UNPARSEABLE question DIRECTLY rather than
  // reading it off `resolvedGateKind === 'disabled'`, which conflates THREE
  // shapes: (a) `resolvable: false`, (b) a `raw` segment, and (c) a
  // `resolvable: true` schema with zero dimension segments. Only (a)/(b) are
  // genuinely broken templates. (c) is ALL-LITERAL and already FINAL — its
  // `catalog_sku` is the real part number with nothing left to pick — so
  // keying off the gate value suppressed a perfectly good part number behind
  // "Please configure…", which the shopper has no way to act on. Splitting
  // `disabled` into `disabled` vs `final` inside `gateConfigSchema` itself is
  // the principled fix (it would also correct this drawer's "Not available
  // yet" empty state, `CatalogSeriesConfigurePage`'s `pickable` check and the
  // cart resolver) and is tracked separately; this predicate is correct
  // locally in the meantime.
  const isPartNumberUnavailable =
    derived?.resolvedSchema !== undefined &&
    (!derived.resolvedSchema.resolvable ||
      derived.resolvedSchema.segments.some((segment) => segment.kind === 'raw'))
  // UID-460 step 2 — the "MISUMI"/"Fictiv" source badge (`format.ts`'s
  // existing, already-shipped mapping — reused verbatim, not reinvented)
  // is only knowable once a row has actually resolved; `CatalogSeries` (the
  // series card data available before that) carries no brand/provenance
  // field at all. Guarded on a real `provenance` value rather than handed
  // straight to `sourceBadgeLabel` (which defaults an absent provenance to
  // "Fictiv") so an item with no provenance on file never gets a fabricated
  // badge.
  const brandLabel = resolvedCard?.sourceable.provenance
    ? sourceBadgeLabel(resolvedCard.sourceable.provenance)
    : null

  // "Recently viewed" regression fix, Case A — a GENUINELY LITERAL resolved
  // row (`isLiteralResolvedRow`: resolved with no `configSchema` at all) is
  // already a finalized SKU with a real `CatalogResultCard` in hand (real
  // `availability`/`category` off the wire, no synthesis needed) — exactly
  // the shape/bar `CatalogSeriesConfigurePage`'s row-expand handler already
  // applies via its own `!hasOpenDimensions(card)` check before calling the
  // SAME `recordCatalogRowView`. This drawer has no row-expand CLICK to key
  // off, so it's keyed on the resolved row's own identity instead (mirrors
  // the `resolvedCardSku`-keyed effect above) — fires once per newly-
  // resolved finalized row, never on every re-render of the same one.
  // Case B (a row that still HAS further dimensions to pick) is handled at
  // Add-to-Cart time in `handleAdd` below, not here — see that comment for
  // why (deferred true reactive-resolve parity, same bucket as the
  // deferred reactive-price fix). A row with an UNPARSEABLE schema is
  // neither case (code-review Critical) and records nothing at all.
  //
  // DEBOUNCED on the SAME `RECENTLY_VIEWED_RECORD_DEBOUNCE_MS` window as
  // `useRecordConfiguratorView` (code-review W1), for the same documented
  // reason: toggling axis pills resolves a succession of DIFFERENT rows, and
  // history dedupes by SKU — so distinct rows are NOT deduped against each
  // other and a scrub would evict genuinely-viewed history (only
  // `MAX_ENTRIES` slots) with rows the shopper never deliberately opened.
  // Only the row that stays resolved for the full window is recorded; the
  // cleanup cancels the timer the moment a different row resolves (or the
  // drawer closes).
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the resolved row's OWN identity (its catalogSku) — the effect body reads `derived`/`resolvedCard` fresh each run, not to re-fire on their own (new-every-render) object identity.
  useEffect(() => {
    if (!derived?.isLiteralResolvedRow || !resolvedCard) return
    const timer = window.setTimeout(() => {
      recordCatalogRowView(resolvedCard)
    }, RECENTLY_VIEWED_RECORD_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [resolvedCardSku])

  // UID-460 step 4 — Add to Cart. Reuses `addCatalogResultToCart` and the
  // toast/cart-count wiring verbatim (the SAME mechanics `ConfiguratorCore`'s
  // own `handleAdd` uses) — no new cart logic. Deliberately does NOT call
  // `onClose()` afterward: the confirmed decision is to leave the drawer
  // open, not auto-close or redirect.
  async function handleAdd() {
    if (!isFullyConfigured || !resolvedCard) return
    setAdding(true)
    try {
      // UID-460 step 4 defect fix — `resolvedCard.sourceable.catalogSku` is
      // the row's raw, unmodified `catalog_sku` TEMPLATE column (e.g.
      // `BFPBG1-P[1.2-3/0.01]-L[2-3/1]-B[1-5/0.1]`), never the final
      // literal part number — see `resolvedCard`'s own doc in
      // `series-configurator-axes.ts`. When this row still carries its OWN
      // dimension segments (`derived.dimensionEntries`, the SAME ones
      // merged into the field list in Step 1 and tracked in
      // `dimensionPicks`), compose the REAL literal SKU from them — the
      // EXACT SAME `validateAndComposeSku` path `ConfiguratorCore.handleAdd`
      // already uses — and resolve THAT, never add `resolvedCard.sourceable`
      // directly. `isFullyConfigured` already guarantees every dimension
      // pick is present and legal (`deriveFieldState`), so `composedSku`
      // here is never `null` in practice; only a schema-less/fully-literal
      // row (`isLiteralResolvedRow`) skips this and adds
      // `resolvedCard.sourceable` as-is, since there is nothing left to
      // compose. A row whose schema is UNPARSEABLE never reaches here at
      // all — `isFullyConfigured` is false for it (code-review Critical),
      // so the button is disabled.
      let sourceableToAdd = resolvedCard.sourceable
      // Keyed on the row's own PICKABLE dimension entries (not on
      // `!isLiteralResolvedRow`), so the compose path is entered only when
      // there is genuinely something to compose.
      //
      // One shape is currently UNREACHABLE here: a `resolvable: true` schema
      // with ZERO dimension segments. `gateConfigSchema` returns `disabled`
      // for it, so `isRowConfigured` — and therefore `isFullyConfigured` —
      // is false, Add to Cart stays disabled and `handleAdd` returns at its
      // own guard above. No such row has been observed coming from the
      // Catalog service in this codebase's fixtures, but nothing in the
      // contract derives or enforces that (no `minItems` on `segments`) —
      // and the drawer's own tests DO construct the shape deliberately. So
      // this predicate does not rely on it being unreachable: it asks "are
      // there dimensions to compose?" rather than inferring the answer from
      // the gate.
      const hadOwnDimensions = derived !== undefined && derived.dimensionEntries.length > 0
      if (hadOwnDimensions) {
        // Defensive: every pick this drawer offers comes from a preset chip
        // or an input already validated against the SAME segment grammar
        // `validateAndComposeSku` uses, and `isFullyConfigured` above
        // requires all of them — so a `null` compose is not reachable
        // today. Still surfaced as an honest error rather than a silent
        // no-op (code-review W3): every OTHER exit path in this function
        // gives the shopper feedback, and a dead click that says nothing is
        // worse than an unexpected message.
        if (composedSku === null) {
          showToast('We couldn’t build a part number from those selections.', 'error')
          return
        }
        sourceableToAdd = await resolveService.resolve(composedSku)
      }
      const result = await addCatalogResultToCart(sourceableToAdd, quantity)
      if (result.lineCount === null) return // 401 — handled globally.
      setLineCount(result.lineCount)
      showToast(`Added ${resultDisplayName(sourceableToAdd)} to cart`)
      // "Recently viewed" regression fix, Case B — this row STILL had its
      // own dimension segments to pick, so Case A's effect (above) never
      // fired for it. Confirmed decision: record on Add-to-Cart SUCCESS
      // (here, after the cart add itself succeeds), reusing THIS resolve
      // call's result rather than adding a new reactive one — true "record
      // on every resolve, independent of Add to Cart" parity is deferred,
      // same bucket as the deferred reactive-price fix. `resolveService
      // .resolve()` hands back a bare `Sourceable` (no `availability`/
      // `category` — see `useConfigurableResolve.ts`'s own doc); reusing
      // `resolvedCard`'s REAL `availability`/`category` here (they don't
      // depend on the dimension pick) rather than synthesizing placeholders
      // like V1 does, since real data is available and preferred
      // (ADR-0090/0091).
      if (hadOwnDimensions) {
        recordCatalogRowView({
          sourceable: sourceableToAdd,
          availability: resolvedCard.availability,
          ...(resolvedCard.category !== undefined ? { category: resolvedCard.category } : {}),
        })
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not add that item.', 'error')
    } finally {
      setAdding(false)
    }
  }

  const isReady = displayConfigurator && state.status !== 'error' && state.status !== 'not_enabled'

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={seriesName}
      maxWidth={760}
      variant="drawer-right"
      footer={
        isReady ? (
          <SeriesConfiguratorFooter
            resolvedCard={resolvedCard}
            isFullyConfigured={isFullyConfigured}
            quantity={quantity}
            onQuantityChange={setQuantity}
            adding={adding}
            onAdd={() => void handleAdd()}
          />
        ) : undefined
      }
      headerContent={
        <div className="configurator-drawer-header-row">
          {brandLabel && <span className="configurator-drawer-brand">{brandLabel}</span>}
          {/* ⚠️ DEMO-ONLY, NOT REAL DATA. No "Volume Discount is available" /
           * "Made to Order" field exists on `CatalogSeries`/`CatalogResultCard`
           * (checked the generated contract) — the FIRST flagged gap here was
           * "omit rather than fabricate" (ADR-0090/0091). Product/design has
           * since explicitly approved these as STATIC demo chrome for THIS
           * pilot (2026-09-04, UID-460 step 2 follow-up): always shown,
           * unconditional on any real per-series fact, and never wired to a
           * real "is this series on volume discount / made to order" field.
           * Revisit if/when a real backing field exists. */}
          <span className="configurator-drawer-badge">Volume Discount is available</span>
          <span className="configurator-drawer-badge">Made to Order</span>
        </div>
      }
    >
      <div className="configurator-drawer-media-row">
        <CatalogResultThumb
          imageRef={resolvedCard?.sourceable.imageRef ?? seriesImageUrl ?? null}
          name={resolvedCard ? resultDisplayName(resolvedCard.sourceable) : seriesName}
          className="configurator-drawer-primary-image"
        />
      </div>

      <h2 className="configurator-drawer-title">{seriesName}</h2>

      <p className="configurator-drawer-part-number">
        <span className="configurator-drawer-part-number-label">Part Number: </span>
        <PartNumberValue
          resolvedCard={resolvedCard}
          composedSku={composedSku}
          unavailable={isPartNumberUnavailable}
        />
      </p>

      {/* ⚠️ DEMO-ONLY, NOT REAL DATA. No Catalog-PDF/CAD download URL field
       * exists on `CatalogSeries`/`CatalogResultCard`/`Sourceable` (checked
       * the generated contract — the same reason `ConfiguratorCore`'s own
       * "CAD preview" tab is an honest empty state). Product/design has
       * since explicitly approved these as static demo links for THIS pilot
       * (2026-09-04, UID-460 step 2 follow-up) — `disabled`, not a real
       * `href`, so nothing reads as live functionality that silently does
       * nothing on click (same treatment as `ConfiguratorCore`'s own
       * "Catalog Page"/"Tech Specs" demo-chrome buttons). Revisit if/when a
       * real file URL exists. */}
      <div className="configurator-drawer-download-links">
        <button type="button" className="configurator-drawer-download-link" disabled>
          <DownloadOutlined aria-hidden="true" />
          Catalog PDF
        </button>
        <button type="button" className="configurator-drawer-download-link" disabled>
          <DownloadOutlined aria-hidden="true" />
          CAD
        </button>
      </div>

      {seriesDescription && <p className="configurator-drawer-description">{seriesDescription}</p>}

      {state.status === 'loading' && lastReady === null && (
        <div className="configurator-drawer-loading" aria-busy="true">
          <span className="sr-only">Loading {seriesName}…</span>
          <Skeleton height={24} width="60%" />
          <Skeleton height={220} variant="block" />
        </div>
      )}

      {state.status === 'error' && (
        <EmptyState
          title="We couldn’t load this series’ configurator"
          detail={state.message}
          action={<Button onClick={state.retry}>Try again</Button>}
        />
      )}

      {state.status === 'not_enabled' && (
        <EmptyState
          title="Not available yet"
          detail="This series doesn’t support the drawer configurator yet."
        />
      )}

      {isReady && displayConfigurator && (
        <>
          <SeriesConfiguratorFieldList
            configurator={displayConfigurator}
            appliedSpecs={appliedSpecs}
            appliedDims={appliedDims}
            dimensionPicks={dimensionPicks}
            onToggleSpec={toggleSpec}
            onPickDim={pickDim}
            onPickDimensionSegment={pickDimensionSegment}
            onReset={resetPicks}
            isPending={isPending}
          />

          <Tabs
            items={OUTER_TABS}
            activeId={activeOuterTabId}
            onChange={setActiveOuterTabId}
            aria-label="Configurator details"
            className="configurator-drawer-outer-tabs"
          >
            {(tabId) => {
              if (tabId === 'cad-preview') {
                // No real CAD asset field exists on `Sourceable`/
                // `CatalogResultCard` (same reason `ConfiguratorCore`'s own
                // "CAD preview" tab is an honest empty state) — an honest
                // empty state here too, never a fabricated preview.
                return (
                  <EmptyState
                    title="CAD preview isn’t available yet"
                    detail="We don’t have a CAD asset on file for this item."
                  />
                )
              }
              if (tabId === 'part-number') {
                return (
                  <p className="configurator-drawer-part-number">
                    <span className="configurator-drawer-part-number-label">Part Number: </span>
                    <PartNumberValue
                      resolvedCard={resolvedCard}
                      composedSku={composedSku}
                      unavailable={isPartNumberUnavailable}
                    />
                  </p>
                )
              }
              return (
                <SeriesSpecTable
                  category={category}
                  categoryGroup={categoryGroup}
                  seriesName={seriesName}
                  brandLabel={brandLabel}
                  configurableDimensionLabels={[
                    ...displayConfigurator.axes.map((axis) => axis.name),
                    ...(derived?.dimensionEntries.map(
                      (entry) => entry.segment.fieldName ?? 'Value',
                    ) ?? []),
                  ]}
                  resolvedCard={resolvedCard}
                />
              )
            }}
          </Tabs>
        </>
      )}
    </Modal>
  )
}

/**
 * The `status: 'ready'` body — one flat `<ul>` of field sections (one per
 * series axis, in the response's own order, PLUS the resolved row's own
 * dimension segments once `candidateCount === 1` — never a second, nested
 * configurator) plus the `candidateCount`-driven outcome below it. See this
 * file's top doc comment for the exact branching rule.
 */
function SeriesConfiguratorFieldList({
  configurator,
  appliedSpecs,
  appliedDims,
  dimensionPicks,
  onToggleSpec,
  onPickDim,
  onPickDimensionSegment,
  onReset,
  isPending,
}: {
  readonly configurator: CatalogSeriesConfigurator
  readonly appliedSpecs: Readonly<Record<string, string>>
  readonly appliedDims: Readonly<Record<string, number>>
  readonly dimensionPicks: SkuPicks
  readonly onToggleSpec: (axisName: string, value: string) => void
  readonly onPickDim: (axisName: string, value: number | null) => void
  readonly onPickDimensionSegment: (segmentIndex: number, value: number | null) => void
  readonly onReset: () => void
  readonly isPending: boolean
}) {
  function isAxisPicked(axis: CatalogSeriesConfiguratorAxis): boolean {
    return axis.name in appliedSpecs || axis.name in appliedDims
  }

  const { resolvedCard, resolvedSchema, resolvedGateKind, dimensionEntries, isFullyConfigured } =
    deriveFieldState(configurator, appliedSpecs, appliedDims, dimensionPicks)
  // Vacuously true when there are no axes at all — a series with nothing to
  // pick has, by definition, already "engaged every available axis", so a
  // `candidateCount` still above 1 in that shape routes to the same
  // residual note as the every-section-picked case, never a narrowing
  // status a shopper could never resolve by picking.
  const everyAxisPicked = configurator.axes.every((axis) => isAxisPicked(axis))

  // The "Configure options:" status-chip row (UID-460 step 3) — the SAME
  // set, in the SAME order, as the field list below; a chip is a plain
  // visual status summary (checkmark once picked), never itself clickable
  // (confirmed against the reference screenshot — no click-to-scroll or
  // other interaction there).
  const fieldChips: ReadonlyArray<{
    readonly key: string
    readonly label: string
    readonly picked: boolean
  }> = [
    ...configurator.axes.map((axis) => ({
      key: axis.name,
      label: axis.name,
      picked: isAxisPicked(axis),
    })),
    ...dimensionEntries.map(({ segment, segmentIndex }) => ({
      key: `dim-${segmentIndex}`,
      label: segment.fieldName ?? 'Value',
      picked: dimensionPicks.has(segmentIndex),
    })),
  ]

  return (
    <>
      {fieldChips.length > 0 && (
        <div className="configurator-drawer-options-row">
          <span className="configurator-drawer-options-label">Configure options:</span>
          <ul className="configurator-drawer-options-chips">
            {fieldChips.map((chip) => (
              <li
                key={chip.key}
                className={cx(
                  'configurator-drawer-options-chip',
                  chip.picked && 'configurator-drawer-options-chip-picked',
                )}
              >
                {chip.picked && <CheckOutlined aria-hidden="true" />}
                {chip.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ul className="configurator-drawer-field-list">
        {configurator.axes.map((axis) => (
          <AxisFieldSection
            key={axis.name}
            axis={axis}
            pickedSpec={appliedSpecs[axis.name]}
            pickedDim={appliedDims[axis.name]}
            onToggleSpec={(value) => onToggleSpec(axis.name, value)}
            onPickDim={(value) => onPickDim(axis.name, value)}
          />
        ))}

        {dimensionEntries.map(({ segment, segmentIndex }) => (
          <DimensionFieldSection
            key={`dim-${segmentIndex}`}
            segment={segment}
            pickedValue={dimensionPicks.get(segmentIndex) ?? null}
            onPick={(value) => onPickDimensionSegment(segmentIndex, value)}
          />
        ))}
      </ul>

      <div className="configurator-drawer-match-banner">
        <p className="configurator-drawer-candidate-count" aria-live="polite">
          {isPending
            ? 'Updating…'
            : configurator.candidateCount === 1
              ? `1 configuration matches your selection${isFullyConfigured ? ' · Fully configured' : ''}`
              : `${configurator.candidateCount} configurations match your selection`}
        </p>
        <Button type="button" variant="link" size="sm" onClick={onReset}>
          Reset
        </Button>
      </div>

      {configurator.candidateCount === 0 && (
        <EmptyState
          title="No matching item"
          detail="Clear a selection above to see other options."
        />
      )}

      {configurator.candidateCount === 1 && resolvedCard === undefined && (
        // Defensive only — the contract guarantees `resolvedCard` whenever
        // `candidateCount === 1`. Never reached against the real BFF.
        <EmptyState
          title="We couldn’t load this item"
          detail="Clear a selection above and try again."
        />
      )}

      {resolvedCard && !resolvedSchema && <ResultCard card={resolvedCard} layout="row" />}

      {resolvedCard && resolvedSchema && resolvedGateKind === 'disabled' && (
        <EmptyState
          title="Not available yet"
          detail="Full configuration for this item isn’t available yet."
        />
      )}

      {configurator.candidateCount > 1 && everyAxisPicked && (
        <EmptyState
          title="A few items still match"
          detail="This selection doesn’t narrow to one item on its own — try changing a selection above."
        />
      )}
    </>
  )
}

/** One resolved row's OWN pickable dimension segments, with their INDEX
 * into `schema.segments` (not a 0..N-1 dimension-only index) — a picked
 * value can never be misaligned with its segment even when segments are
 * interleaved with literals. */
function pickableDimensionEntries(
  schema: ConfigSchema,
): ReadonlyArray<{ readonly segment: ConfigDimensionSegment; readonly segmentIndex: number }> {
  return schema.segments
    .map((segment, segmentIndex) => ({ segment, segmentIndex }))
    .filter(
      (entry): entry is { segment: ConfigDimensionSegment; segmentIndex: number } =>
        entry.segment.kind === 'range' || entry.segment.kind === 'enum',
    )
}

/**
 * One series-level field — a single `<fieldset>`, ALWAYS fully expanded,
 * ALWAYS showing its complete pill list (UID-460 step 1: no accordion, no
 * collapse, no summary chip). Picking a value only toggles that pill's own
 * selected style; the section's shape never changes.
 */
function AxisFieldSection({
  axis,
  pickedSpec,
  pickedDim,
  onToggleSpec,
  onPickDim,
}: {
  readonly axis: CatalogSeriesConfiguratorAxis
  readonly pickedSpec: string | undefined
  readonly pickedDim: number | undefined
  readonly onToggleSpec: (value: string) => void
  readonly onPickDim: (value: number | null) => void
}) {
  return (
    <li className="configurator-drawer-section">
      <fieldset className="configurator-drawer-variant-selector">
        <legend>{axis.name}</legend>

        {axis.values.length > 0 && (
          <div className="configurator-core-pills">
            {axis.values.map((value) => {
              const selected = pickedSpec === value.value
              return (
                <button
                  key={value.value}
                  type="button"
                  aria-pressed={selected}
                  className={cx(
                    'configurator-core-pill',
                    selected && 'configurator-core-pill-selected',
                  )}
                  onClick={() => onToggleSpec(value.value)}
                >
                  {value.value}
                  <span className="configurator-drawer-axis-count" aria-hidden="true">
                    {' '}
                    ({value.itemCount})
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {axis.intervals && axis.intervals.length > 0 && (
          <RangeWindowPicker
            fieldName={axis.name}
            windows={axis.intervals}
            pickedValue={pickedDim ?? null}
            onPick={onPickDim}
          />
        )}
      </fieldset>
    </li>
  )
}

/**
 * One resolved row's OWN continuous (`range`) or discrete-numeric (`enum`)
 * dimension segment, rendered in the SAME flat list and the SAME always-
 * expanded shape as {@link AxisFieldSection} above — this is the merge that
 * replaces mounting a second, nested `ConfiguratorCore` (UID-460 step 1).
 */
function DimensionFieldSection({
  segment,
  pickedValue,
  onPick,
}: {
  readonly segment: ConfigDimensionSegment
  readonly pickedValue: number | null
  readonly onPick: (value: number | null) => void
}) {
  const label = segment.fieldName ?? 'Value'

  return (
    <li className="configurator-drawer-section">
      <fieldset className="configurator-drawer-variant-selector">
        <legend>{label}</legend>

        {segment.kind === 'enum' && (
          <div className="configurator-core-pills">
            {segment.values.map((value) => {
              const selected = pickedValue === value
              return (
                <button
                  key={value}
                  type="button"
                  aria-pressed={selected}
                  className={cx(
                    'configurator-core-pill',
                    selected && 'configurator-core-pill-selected',
                  )}
                  onClick={() => onPick(selected ? null : value)}
                >
                  {formatValueToken(segment, value)}
                </button>
              )
            })}
          </div>
        )}

        {segment.kind === 'range' && (
          <RangeWindowPicker
            fieldName={label}
            windows={[segment]}
            pickedValue={pickedValue}
            onPick={onPick}
          />
        )}
      </fieldset>
    </li>
  )
}

/** Format a picked numeric value at the decimal precision implied by
 * whichever window it falls within (or the first window, defensively, if
 * none contains it — never reached for a value this drawer itself picked,
 * on-grid by construction). */
function formatWindowValue(windows: readonly RangeWindow[], value: number): string {
  const relevant =
    windows.find((window) => value >= window.min && value <= window.max) ?? windows[0]
  const segment: ConfigDimensionSegment = {
    kind: 'range',
    min: relevant?.min ?? value,
    max: relevant?.max ?? value,
    step: relevant?.step ?? 1,
  }
  return formatValueToken(segment, value)
}

/** The window the typed/picked `value` falls inside, or — for bounds
 * display only while the value doesn't land in any window — whichever
 * window it is numerically closest to. Never authoritative: the pick
 * itself is validated against the SAME segment grammar the resolve step
 * uses (`isValueLegal`, shared with `configurator-sku.ts`). */
function nearestWindow(
  windows: readonly RangeWindow[],
  value: number | null,
): RangeWindow | undefined {
  if (value === null || !Number.isFinite(value)) return windows[0]
  const containing = windows.find((window) => value >= window.min && value <= window.max)
  if (containing) return containing
  return windows.reduce<RangeWindow | undefined>((closest, window) => {
    const distance = value < window.min ? window.min - value : value - window.max
    if (closest === undefined) return window
    const closestDistance = value < closest.min ? closest.min - value : value - closest.max
    return distance < closestDistance ? window : closest
  }, undefined)
}

function isWindowValueLegal(windows: readonly RangeWindow[], value: number): boolean {
  return windows.some((window) =>
    isValueLegal({ kind: 'range', min: window.min, max: window.max, step: window.step }, value),
  )
}

/**
 * UID-322 — the continuous half of a field: one quick-pick pill row PER
 * window (never merged/flattened — two windows can be genuinely non-
 * contiguous or carry divergent steps, see `CatalogSeriesConfiguratorAxisInterval`'s
 * doc), plus one shared free-text input whose displayed min/max/step bounds
 * relax to whichever window the CURRENTLY TYPED value falls in (or is
 * nearest to) — never a fixed single window. Shared by a series-level
 * `intervals` axis AND a resolved row's OWN `range` `configSchema` segment
 * (always exactly one window in that case) — same rendering either way.
 */
function RangeWindowPicker({
  fieldName,
  windows,
  pickedValue,
  onPick,
}: {
  readonly fieldName: string
  readonly windows: readonly RangeWindow[]
  readonly pickedValue: number | null
  readonly onPick: (value: number | null) => void
}) {
  const inputId = useId()
  const [rawInput, setRawInput] = useState(
    pickedValue !== null ? formatWindowValue(windows, pickedValue) : '',
  )
  const parsed = rawInput.trim() === '' ? null : Number(rawInput)
  const hasTypedValue = parsed !== null && Number.isFinite(parsed)
  const bounds = nearestWindow(windows, hasTypedValue ? parsed : pickedValue)

  return (
    <div className="configurator-drawer-interval-picker">
      {windows.map((window, windowIndex) => {
        const segment: ConfigDimensionSegment = {
          kind: 'range',
          min: window.min,
          max: window.max,
          step: window.step,
        }
        const presets = computeRangePresets(segment)
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: two windows of one field can share identical min/max/step (divergent-step overlap) with no other stable key.
          <div key={windowIndex} className="configurator-core-range-picker">
            <div className="configurator-core-pills">
              {presets.map((value) => {
                const selected = pickedValue === value
                return (
                  <button
                    key={value}
                    type="button"
                    className={cx(
                      'configurator-core-pill',
                      selected && 'configurator-core-pill-selected',
                    )}
                    aria-pressed={selected}
                    onClick={() => {
                      setRawInput(formatValueToken(segment, value))
                      onPick(value)
                    }}
                  >
                    {formatValueToken(segment, value)}
                  </button>
                )
              })}
            </div>
          </div>
        )
      })}
      <Input
        label={fieldName}
        hideLabel
        className="configurator-core-custom-input"
        id={`${inputId}-dim`}
        type="number"
        inputMode="decimal"
        min={bounds?.min}
        max={bounds?.max}
        step={bounds?.step}
        placeholder={windows.map((window) => `${window.min}–${window.max}`).join(' or ')}
        value={rawInput}
        error={
          hasTypedValue && !isWindowValueLegal(windows, parsed)
            ? `Enter a value between ${bounds?.min} and ${bounds?.max}`
            : undefined
        }
        onChange={(event) => {
          const text = event.target.value
          setRawInput(text)
          const nextParsed = text.trim() === '' ? null : Number(text)
          const legal =
            nextParsed !== null &&
            Number.isFinite(nextParsed) &&
            isWindowValueLegal(windows, nextParsed)
          onPick(legal ? nextParsed : null)
        }}
      />
    </div>
  )
}

/** "Part Number: <resolved SKU>" once fully resolved, or the placeholder
 * copy before that — shared by the always-visible header line and the
 * "Part number" tab panel (mirroring `ConfiguratorCore`'s own
 * `PartNumberLine` dual-use precedent) so both read from the same one
 * `resolvedCard`, never two independent conditions that could drift. */
function PartNumberValue({
  resolvedCard,
  composedSku,
  unavailable = false,
}: {
  readonly resolvedCard: CatalogResultCard | undefined
  /** UID-460 step 4 defect fix — the resolved row's OWN dimension picks
   * (`dimensionPicks`), client-side composed into a real literal SKU via
   * the SAME `validateAndComposeSku` the Add to Cart fix uses. `null`
   * (no dimension segments, or one still unpicked/illegal) falls through
   * to `resolvedCard.sourceable.catalogSku` below UNCHANGED — the
   * incomplete-picks display is deliberately not touched by this fix. */
  readonly composedSku?: string | null
  /** UID-460 code-review follow-up (W-B) — the resolved row's schema is
   * UNPARSEABLE (`resolvable: false`, or a `raw` segment present): a terminal
   * state, so its raw `catalogSku` TEMPLATE must never be presented as a part
   * number. Narrower than `gateConfigSchema`'s `disabled` on purpose, which
   * also covers an all-literal already-final schema whose `catalogSku` IS the
   * real part number — see `isPartNumberUnavailable` at the one call-site
   * derivation. */
  readonly unavailable?: boolean
}) {
  const sku = unavailable ? undefined : (composedSku ?? resolvedCard?.sourceable.catalogSku)
  return sku ? (
    <span className="configurator-drawer-part-number-value">{sku}</span>
  ) : (
    <span className="configurator-drawer-part-number-placeholder">
      Please configure to get your part number
    </span>
  )
}

/**
 * UID-460 step 4 — the persistent footer (`Modal`'s own `footer` slot,
 * matching the legacy reference's price/qty/Add-to-Cart bar). Unit price
 * and ship estimate come straight off `resolvedCard.sourceable`/
 * `.availability` — real data, never re-resolved through a second call —
 * but ONLY once `isFullyConfigured` (the SAME definition `deriveFieldState`
 * computes for the Step 3 banner; this is deliberately not a second,
 * looser "resolvedCard exists" check, since a row can resolve via `specs`
 * alone while its own dimension picks are still open). A placeholder dash
 * renders before that.
 *
 * No overflow ("...") menu: checked `ResultCard.tsx` and every other
 * `DropdownMenu` consumer in this codebase for a reusable "catalog item
 * footer actions" menu and found none with options that would honestly
 * apply here — flagged back rather than fabricating menu items (e.g. "Save
 * for later", "Share") with no real destination.
 */
function SeriesConfiguratorFooter({
  resolvedCard,
  isFullyConfigured,
  quantity,
  onQuantityChange,
  adding,
  onAdd,
}: {
  readonly resolvedCard: CatalogResultCard | undefined
  readonly isFullyConfigured: boolean
  readonly quantity: number
  readonly onQuantityChange: (next: number) => void
  readonly adding: boolean
  readonly onAdd: () => void
}) {
  const resolvedPrice = isFullyConfigured ? resolvedCard?.sourceable.indicativePrice : undefined
  const shipEstimate =
    isFullyConfigured && resolvedCard ? leadTimeLabel(resolvedCard.availability) : undefined

  return (
    <div className="configurator-drawer-footer">
      <div className="configurator-drawer-footer-price-row">
        <span className="configurator-drawer-footer-label">Unit Price</span>
        {resolvedPrice ? (
          <span className="configurator-drawer-footer-price">
            {resolvedPrice.currency} {resolvedPrice.amount}
          </span>
        ) : (
          <span className="configurator-drawer-footer-placeholder">—</span>
        )}
      </div>

      <div className="configurator-drawer-footer-price-row">
        <span className="configurator-drawer-footer-label">Ship Estimate</span>
        {shipEstimate ? (
          <span className="configurator-drawer-footer-ship">{shipEstimate}</span>
        ) : (
          <span className="configurator-drawer-footer-placeholder">—</span>
        )}
      </div>

      <QuantityStepper
        label={`Quantity for ${resolvedCard ? resultDisplayName(resolvedCard.sourceable) : 'this item'}`}
        value={quantity}
        onChange={onQuantityChange}
      />

      <Button
        type="button"
        variant="primary"
        disabled={!isFullyConfigured}
        loading={adding}
        onClick={onAdd}
      >
        Add to Cart
      </Button>
    </div>
  )
}

/**
 * UID-460 step 4 — the "Drawing / Specs" tab's static spec table (Category/
 * Group/Series/Brand/Configurable dimensions/Materials/Lead time/Volume
 * discount), re-verified against real data field-by-field:
 * - Category/Series: the route param / series name this drawer already has.
 * - Group: `CatalogSeriesPage`'s own taxonomy group, threaded through as
 *   `categoryGroup` — omitted (never fabricated) when the category has none.
 * - Brand: the SAME resolve-only `sourceBadgeLabel` mapping as the header
 *   chip (Step 2) — a dash before a row resolves, per that same decision.
 * - Configurable dimensions: the REAL field names driving the field list
 *   above (series axes + the resolved row's own dimension segments) — never
 *   a second, independent list.
 * - Materials: a real `Material`-named entry on the resolved row's own
 *   `specs` bag, when one exists — a dash otherwise, never invented.
 * - Lead time: the real `leadTimeLabel(resolvedCard.availability)` helper
 *   (`format.ts`, already used elsewhere) — NOT `ConfiguratorCore`'s own
 *   static "Ships in 1 day~" line, which that file's own comment flags as
 *   not real; this drawer's `resolvedCard` carries a real `availability`
 *   `ConfiguratorCore`'s post-resolve `Sourceable` does not.
 * - Volume discount: ⚠️ DEMO-ONLY, NOT REAL DATA — the SAME approved static
 *   demo-chrome decision (2026-09-04, Step 2 follow-up) as the header's
 *   "Volume Discount is available" badge. No real per-series field backs
 *   this; static for THIS pilot.
 */
function SeriesSpecTable({
  category,
  categoryGroup,
  seriesName,
  brandLabel,
  configurableDimensionLabels,
  resolvedCard,
}: {
  readonly category: string
  readonly categoryGroup: string | undefined
  readonly seriesName: string
  readonly brandLabel: string | null
  readonly configurableDimensionLabels: readonly string[]
  readonly resolvedCard: CatalogResultCard | undefined
}) {
  const material = resolvedCard?.specs?.find((spec) => spec.name === 'Material')?.value
  const leadTime = resolvedCard ? leadTimeLabel(resolvedCard.availability) : undefined

  return (
    <dl className="configurator-drawer-spec-table">
      <div className="configurator-drawer-spec-row">
        <dt>Category</dt>
        <dd>{category}</dd>
      </div>
      {categoryGroup && (
        <div className="configurator-drawer-spec-row">
          <dt>Group</dt>
          <dd>{categoryGroup}</dd>
        </div>
      )}
      <div className="configurator-drawer-spec-row">
        <dt>Series</dt>
        <dd>{seriesName}</dd>
      </div>
      <div className="configurator-drawer-spec-row">
        <dt>Brand</dt>
        <dd>{brandLabel ?? '—'}</dd>
      </div>
      <div className="configurator-drawer-spec-row">
        <dt>Configurable dimensions</dt>
        <dd>
          {configurableDimensionLabels.length > 0 ? configurableDimensionLabels.join(', ') : '—'}
        </dd>
      </div>
      <div className="configurator-drawer-spec-row">
        <dt>Materials</dt>
        <dd>{material ?? '—'}</dd>
      </div>
      <div className="configurator-drawer-spec-row">
        <dt>Lead time</dt>
        <dd>{leadTime ?? '—'}</dd>
      </div>
      <div className="configurator-drawer-spec-row">
        <dt>Volume discount</dt>
        <dd>Available</dd>
      </div>
    </dl>
  )
}
